import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Hoodie,
  Trash,
  ArrowLeft,
  Tag,
  Storefront,
  Package,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getItem, updateItem, deleteItem, formatPrice, imageSrc } from "../lib/api";

const statusStyles = {
  draft: "bg-[#EBE8E0]",
  listed: "bg-[#B4F0A7]",
  sold_out: "bg-[#FFDF85]",
};

const statusLabel = (s) => (s === "sold_out" ? "sold out" : s);

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const it = await getItem(id);
      setItem(it);
      setForm({
        suggested_title: it.suggested_title,
        suggested_description: it.suggested_description,
        listing_price: it.listing_price ?? "",
        cost_price: it.cost_price ?? 0,
        quantity: it.quantity ?? 1,
      });
    } catch {
      toast.error("Item not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const updateStatus = async (status) => {
    if (status === "listed" && !(item.listing_price > 0)) {
      return toast.error("Set a listing price before going live");
    }
    try {
      const u = await updateItem(id, { status });
      setItem(u);
      toast.success(`Status: ${statusLabel(status)}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const saveEdits = async () => {
    try {
      const u = await updateItem(id, {
        suggested_title: form.suggested_title,
        suggested_description: form.suggested_description,
        listing_price: form.listing_price ? Number(form.listing_price) : null,
        cost_price: form.cost_price ? Number(form.cost_price) : 0,
        quantity: Math.max(1, Number(form.quantity) || 1),
      });
      setItem(u);
      setEditing(false);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await deleteItem(id);
      toast.success("Deleted");
      navigate("/dashboard");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading || !item)
    return (
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="nb-card bg-white">Loading…</div>
      </div>
    );

  const stock = Math.max(0, (item.quantity || 1) - (item.quantity_sold || 0));
  const profit = (item.revenue || 0) - (item.cost_price || 0) * (item.quantity_sold || 0);

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 sm:px-10">
      <Link
        to="/dashboard"
        data-testid="back-link"
        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline"
      >
        <ArrowLeft weight="bold" size={16} /> Back to dashboard
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="nb-card bg-white">
            <div className="flex h-[420px] w-full items-center justify-center overflow-hidden rounded-xl border-[3px] border-[#0A0A0A] bg-[#FDFBF7]">
              {item.image_base64 ? (
                <img src={imageSrc(item)} alt={item.product_name} className="h-full w-full object-contain" />
              ) : (
                <Hoodie weight="duotone" size={88} />
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <span className={`nb-badge ${statusStyles[item.status]}`}>{statusLabel(item.status)}</span>
              <div className="flex flex-wrap gap-2">
                {item.status === "draft" && (
                  <button
                    data-testid="mark-listed"
                    onClick={() => updateStatus("listed")}
                    className="nb-btn nb-btn-secondary inline-flex items-center gap-2"
                  >
                    <Storefront weight="bold" size={16} /> List it
                  </button>
                )}
                {item.status === "listed" && (
                  <button
                    data-testid="mark-draft"
                    onClick={() => updateStatus("draft")}
                    className="nb-btn nb-btn-ghost inline-flex items-center gap-2"
                  >
                    Unlist
                  </button>
                )}
                {item.status === "listed" && (
                  <Link
                    to={`/listing/${item.id}`}
                    data-testid="view-public"
                    className="nb-btn inline-flex items-center gap-2"
                  >
                    View public listing
                  </Link>
                )}
                <button
                  data-testid="delete-item"
                  onClick={onDelete}
                  className="nb-btn nb-btn-danger inline-flex items-center gap-2"
                >
                  <Trash weight="bold" size={16} /> Delete
                </button>
              </div>
            </div>
          </div>

          <div className="nb-card bg-[#FFDF85]">
            <div className="flex items-center gap-2">
              <Tag weight="duotone" size={20} />
              <span className="nb-label">Estimated value</span>
            </div>
            <div className="mt-2 font-display text-3xl font-black tracking-tighter">
              {formatPrice(item.estimated_value_min)} – {formatPrice(item.estimated_value_max)}
            </div>
            <div className="mt-3 text-sm font-medium">
              Best platform:{" "}
              <span className="rounded-md border-[2px] border-[#0A0A0A] bg-white px-2 py-0.5 font-bold">
                {item.suggested_platform}
              </span>
            </div>
            {item.platform_reasoning && (
              <p className="mt-2 text-sm font-medium text-[#4A4A4A]">{item.platform_reasoning}</p>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="nb-card bg-white">
            <div className="flex items-start justify-between">
              <span className="nb-label">Listing</span>
              <button
                data-testid="edit-btn"
                onClick={() => setEditing((v) => !v)}
                className="nb-badge cursor-pointer bg-[#D4C4FB]"
              >
                {editing ? "Cancel" : "Edit"}
              </button>
            </div>

            {!editing ? (
              <>
                <h1 className="mt-2 font-display text-3xl font-black leading-tight tracking-tight sm:text-4xl">
                  {item.suggested_title}
                </h1>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.brand && <span className="nb-badge bg-[#D4C4FB]">{item.brand}</span>}
                  {item.category && <span className="nb-badge bg-white">{item.category}</span>}
                  {item.condition && <span className="nb-badge bg-[#B4F0A7]">{item.condition}</span>}
                </div>
                <p className="mt-5 whitespace-pre-line text-base font-medium leading-relaxed">
                  {item.suggested_description}
                </p>
              </>
            ) : (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="nb-label">Title</label>
                  <input
                    data-testid="edit-form-title"
                    value={form.suggested_title || ""}
                    onChange={(e) => setForm({ ...form, suggested_title: e.target.value })}
                    className="nb-input mt-2"
                  />
                </div>
                <div>
                  <label className="nb-label">Description</label>
                  <textarea
                    data-testid="edit-form-description"
                    value={form.suggested_description || ""}
                    onChange={(e) => setForm({ ...form, suggested_description: e.target.value })}
                    rows={5}
                    className="nb-input mt-2 resize-y"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="nb-label">Price (£)</label>
                    <input
                      data-testid="edit-form-price"
                      type="number"
                      value={form.listing_price ?? ""}
                      onChange={(e) => setForm({ ...form, listing_price: e.target.value })}
                      className="nb-input mt-2"
                    />
                  </div>
                  <div>
                    <label className="nb-label">Cost</label>
                    <input
                      type="number"
                      value={form.cost_price ?? 0}
                      onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                      className="nb-input mt-2"
                    />
                  </div>
                  <div>
                    <label className="nb-label">Quantity</label>
                    <input
                      data-testid="edit-form-quantity"
                      type="number"
                      min={1}
                      value={form.quantity ?? 1}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className="nb-input mt-2"
                    />
                  </div>
                </div>
                <button
                  data-testid="save-edits"
                  onClick={saveEdits}
                  className="nb-btn nb-btn-secondary inline-flex items-center gap-2"
                >
                  <CheckCircle weight="bold" size={16} /> Save
                </button>
              </div>
            )}
          </div>

          <div className="nb-card bg-[#D4C4FB]">
            <span className="nb-label">Stock & profit</span>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Listing price</div>
                <div className="font-display text-2xl font-black">
                  {item.listing_price ? formatPrice(item.listing_price) : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Cost per unit</div>
                <div className="font-display text-2xl font-black">{formatPrice(item.cost_price || 0)}</div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">In stock</div>
                <div className="font-display text-2xl font-black flex items-center gap-2">
                  <Package weight="duotone" size={20} /> {stock} / {item.quantity}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Sold</div>
                <div className="font-display text-2xl font-black">{item.quantity_sold || 0}</div>
              </div>
              <div className="col-span-2 rounded-lg border-[3px] border-[#0A0A0A] bg-white p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Revenue</div>
                <div className="font-display text-3xl font-black tracking-tighter">{formatPrice(item.revenue || 0)}</div>
                <div className="mt-2 text-xs font-bold uppercase tracking-wider text-[#4A4A4A]">Profit</div>
                <div className="font-display text-2xl font-black tracking-tighter">{formatPrice(profit)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
